// EditPermissionModal.tsx — Co Marca — Editar Permiso
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { updateModelPermission, ModelPermission, fetchModels } from '../../services/modelPermissionsService';

interface EditPermissionModalProps {
    isOpen: boolean; onClose: () => void;
    userLevelId: number; initialData: ModelPermission;
    onPermissionUpdated?: () => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const PERM_KEYS = ['can_create','can_read','can_update','can_delete'] as const;
const PERM_LABELS: Record<string,{ label:string; color:string; bg:string }> = {
    can_create: { label:'Crear',    color:colors.secondary,      bg:`${colors.secondary}12` },
    can_read:   { label:'Ver',      color:colors.secondaryLight,  bg:`${colors.secondaryLight}14` },
    can_update: { label:'Editar',   color:colors.accentDark,      bg:`${colors.accentDark}12` },
    can_delete: { label:'Eliminar', color:colors.primary,         bg:`${colors.primary}10` },
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.epm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes epmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes epmSlideUp{from{opacity:0;transform:translateY(24px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.epm-overlay{animation:epmFadeIn .22s ease both;}
.epm-dialog{animation:epmSlideUp .32s cubic-bezier(.16,1,.3,1) both;}
.epm-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;}
.epm-input:focus{border-color:${colors.secondary};box-shadow:0 0 0 3px ${colors.secondary}12;background:#fff;}
.epm-input:disabled{background:#f1f5f9;color:#94a3b8;cursor:not-allowed;}
.epm-select{appearance:none;}
.epm-btn-primary{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.secondary},#004d7a);color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(97, 37, 123,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.epm-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(97, 37, 123,.34);}
.epm-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.epm-btn-ghost{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;}
.epm-btn-ghost:hover{background:#f1f5f9;}
.custom-scroll::-webkit-scrollbar{width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97, 37, 123,.5);}
`;

const EditPermissionModal: React.FC<EditPermissionModalProps> = ({ isOpen, onClose, userLevelId, initialData, onPermissionUpdated }) => {
    const [modelOptions, setModelOptions] = useState<{model_name:string;verbose_name:string}[]>([]);
    const [modelName, setModelName]       = useState(initialData.model_name||'');
    const [permissions, setPermissions]   = useState({ can_create:initialData.can_create, can_read:initialData.can_read, can_update:initialData.can_update, can_delete:initialData.can_delete });
    const [loading, setLoading]           = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchModels().then(setModelOptions).catch(console.error);
            setModelName(initialData.model_name||'');
            setPermissions({ can_create:initialData.can_create, can_read:initialData.can_read, can_update:initialData.can_update, can_delete:initialData.can_delete });
        }
    }, [isOpen, initialData]);

    const toggle = (key: string) => setPermissions(p=>({ ...p, [key]:!p[key as keyof typeof p] }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); setLoading(true);
        try {
            await updateModelPermission(initialData.id, { user_level:userLevelId, model_name:modelName, ...permissions });
            onPermissionUpdated?.(); onClose();
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="epm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="epm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>
                <div className="epm-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'520px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.secondary}30` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Editar Permiso</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Nivel {userLevelId} · <strong style={{ color:'#374151' }}>{initialData.model_name}</strong></p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}>
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                <ShieldCheck size={11} color={colors.secondary}/> Nivel de usuario
                            </label>
                            <input type="text" value={`Nivel ${userLevelId}`} disabled className="epm-input"/>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Modelo *</label>
                            <select required value={modelName} onChange={e=>setModelName(e.target.value)} className="epm-input epm-select">
                                <option value="" disabled>Selecciona un modelo...</option>
                                {modelOptions.map(o=><option key={o.model_name} value={o.model_name}>{o.verbose_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'10px' }}>Permisos</label>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                                {PERM_KEYS.map(key=>{
                                    const cfg = PERM_LABELS[key];
                                    const val = permissions[key];
                                    return (
                                        <div key={key} style={{ background:val?cfg.bg:'#f8fafc', border:`1.5px solid ${val?cfg.color+'30':'#f1f5f9'}`, borderRadius:'12px', padding:'12px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', transition:'all .2s' }}>
                                            <div>
                                                <p style={{ fontSize:'.82rem', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>Puede {cfg.label}</p>
                                                <p style={{ fontSize:'.68rem', fontWeight:'600', color:val?cfg.color:'#94a3b8' }}>
                                                    {val?'● Permitido':'○ No permitido'}
                                                </p>
                                            </div>
                                            <div onClick={()=>toggle(key)} style={{ width:'40px', height:'22px', borderRadius:'100px', background:val?cfg.color:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                                <div style={{ position:'absolute', top:'3px', left:val?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="epm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="epm-btn-primary">
                                {loading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</> : <><CheckCircle2 size={14}/> Guardar Cambios</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default EditPermissionModal;